import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getReviewsByTarget, addReview, getCandidateById, getCompanyById } from '../services/api';

const ReviewPage = () => {
  const { targetId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [target, setTarget] = useState(null);
  const [targetType, setTargetType] = useState('');
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    rating: 5,
    comment: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [hoveredRating, setHoveredRating] = useState(0);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);

      // Try to load as candidate first
      let candidateResult = await getCandidateById(targetId);
      if (candidateResult.success && candidateResult.candidate) {
        setTarget(candidateResult.candidate);
        setTargetType('candidate');
      } else {
        // Try to load as company
        let companyResult = await getCompanyById(targetId);
        if (companyResult.success && companyResult.company) {
          setTarget(companyResult.company);
          setTargetType('company');
        }
      }

      // Load existing reviews
      const reviewsResult = await getReviewsByTarget(targetId);
      if (reviewsResult.success) {
        setReviews(reviewsResult.reviews);
      }

      setLoading(false);
    };

    loadData();
  }, [targetId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'rating' ? parseInt(value) : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage('');

    if (!currentUser) {
      setMessage('You must be logged in to submit a review');
      setSubmitting(false);
      return;
    }

    const reviewData = {
      reviewerId: currentUser.uid,
      reviewerName: currentUser.displayName || currentUser.email,
      reviewerRole: currentUser.role,
      targetId: targetId,
      targetName: target.name,
      targetType: targetType,
      rating: parseInt(formData.rating),
      comment: formData.comment
    };

    const result = await addReview(reviewData);

    if (result.success) {
      setMessage('Review submitted successfully!');
      setFormData({ rating: 5, comment: '' });

      // Reload reviews
      const reviewsResult = await getReviewsByTarget(targetId);
      if (reviewsResult.success) {
        setReviews(reviewsResult.reviews);
      }

      setTimeout(() => {
        setMessage('');
      }, 3000);
    } else {
      setMessage('Error submitting review. Please try again.');
    }

    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-white to-pink-50">
        <div className="text-center">
          <div className="animate-spin text-6xl mb-4">⭐</div>
          <div className="text-xl font-semibold text-gray-700">Loading reviews...</div>
        </div>
      </div>
    );
  }

  if (!target) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-white to-pink-50">
        <div className="text-center bg-white p-12 rounded-2xl shadow-2xl">
          <div className="text-6xl mb-4">🔍</div>
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Target Not Found</h2>
          <p className="text-gray-600 mb-6">We couldn't find what you're looking for.</p>
          <Link
            to="/dashboard"
            className="inline-block bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-3 rounded-xl font-bold hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            ← Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  const averageRating = reviews.length > 0
    ? (reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length).toFixed(1)
    : 'N/A';

  // Calculate rating distribution
  const ratingDistribution = [5, 4, 3, 2, 1].map(rating => {
    const count = reviews.filter(r => r.rating === rating).length;
    const percentage = reviews.length > 0 ? (count / reviews.length) * 100 : 0;
    return { rating, count, percentage };
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-rose-600 rounded-2xl shadow-2xl p-8 mb-8 text-white">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="mb-6 md:mb-0">
              <div className="flex items-center mb-3">
                <span className="text-5xl mr-4">⭐</span>
                <div>
                  <h1 className="text-4xl font-bold mb-1">
                    Reviews & Ratings
                  </h1>
                  <p className="text-purple-100 text-lg">
                    for {target.name}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white bg-opacity-20 backdrop-blur-lg rounded-2xl p-6 text-center border-2 border-white border-opacity-30">
              <div className="text-6xl font-bold mb-2">{averageRating}</div>
              <div className="flex items-center justify-center mb-2">
                {[...Array(5)].map((_, i) => (
                  <span
                    key={i}
                    className={`text-2xl ${
                      i < Math.round(parseFloat(averageRating)) ? 'text-yellow-300' : 'text-white text-opacity-30'
                    }`}
                  >
                    ★
                  </span>
                ))}
              </div>
              <p className="text-sm text-purple-100">{reviews.length} {reviews.length === 1 ? 'Review' : 'Reviews'}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Rating Distribution */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-xl p-6 border-t-4 border-purple-500 sticky top-8">
              <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
                <span className="bg-purple-500 text-white p-2 rounded-lg mr-3">📊</span>
                Rating Distribution
              </h3>
              <div className="space-y-3">
                {ratingDistribution.map(({ rating, count, percentage }) => (
                  <div key={rating} className="flex items-center gap-3">
                    <div className="flex items-center w-16">
                      <span className="text-sm font-semibold text-gray-700">{rating}</span>
                      <span className="text-yellow-500 ml-1">★</span>
                    </div>
                    <div className="flex-1">
                      <div className="bg-gray-200 rounded-full h-3 overflow-hidden">
                        <div
                          className="bg-gradient-to-r from-yellow-400 to-orange-500 h-full rounded-full transition-all duration-500"
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                    </div>
                    <div className="w-12 text-right">
                      <span className="text-sm font-medium text-gray-600">{count}</span>
                    </div>
                  </div>
                ))}
              </div>

              {currentUser && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <a
                    href="#write-review"
                    className="block w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-xl font-bold hover:from-purple-700 hover:to-pink-700 transition-all duration-300 shadow-md hover:shadow-lg text-center"
                  >
                    ✍️ Write a Review
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Reviews */}
          <div className="lg:col-span-2 space-y-8">
            {/* Write Review Form */}
            {currentUser && (
              <div id="write-review" className="bg-white rounded-2xl shadow-xl p-8 border-l-4 border-purple-500">
                <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                  <span className="bg-purple-500 text-white p-2 rounded-lg mr-3">✍️</span>
                  Share Your Experience
                </h2>

                {message && (
                  <div className={`mb-6 p-4 rounded-xl shadow-md ${
                    message.includes('success')
                      ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 text-green-700'
                      : 'bg-gradient-to-r from-red-50 to-rose-50 border-l-4 border-red-500 text-red-700'
                  }`}>
                    <div className="flex items-center">
                      <span className="text-2xl mr-3">{message.includes('success') ? '✅' : '❌'}</span>
                      <span className="font-medium">{message}</span>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Rating Input */}
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6">
                    <label className="block text-gray-800 font-bold mb-4 text-lg">
                      How would you rate your experience? *
                    </label>
                    <div className="flex items-center justify-center space-x-3">
                      {[1, 2, 3, 4, 5].map((value) => (
                        <label
                          key={value}
                          className="cursor-pointer transform transition-all duration-200 hover:scale-125"
                          onMouseEnter={() => setHoveredRating(value)}
                          onMouseLeave={() => setHoveredRating(0)}
                        >
                          <input
                            type="radio"
                            name="rating"
                            value={value}
                            checked={formData.rating === value}
                            onChange={handleChange}
                            className="sr-only"
                          />
                          <span
                            className={`text-5xl transition-all duration-200 ${
                              (hoveredRating > 0 ? hoveredRating >= value : formData.rating >= value)
                                ? 'text-yellow-500 drop-shadow-lg'
                                : 'text-gray-300'
                            }`}
                          >
                            ★
                          </span>
                        </label>
                      ))}
                    </div>
                    <div className="text-center mt-4">
                      <span className="inline-block bg-purple-600 text-white px-4 py-2 rounded-full text-sm font-bold">
                        {hoveredRating > 0 ? hoveredRating : formData.rating} out of 5 stars
                      </span>
                    </div>
                  </div>

                  {/* Comment Input */}
                  <div>
                    <label className="block text-gray-800 font-bold mb-3 text-lg">
                      Tell us about your experience *
                    </label>
                    <textarea
                      name="comment"
                      value={formData.comment}
                      onChange={handleChange}
                      rows="6"
                      className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all text-gray-700 placeholder-gray-400"
                      placeholder="Share the details of your experience. What went well? What could be improved?"
                      required
                    ></textarea>
                    <p className="text-sm text-gray-500 mt-2">💡 Minimum 10 characters required</p>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-4 rounded-xl font-bold text-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    {submitting ? (
                      <span className="flex items-center justify-center">
                        <span className="animate-spin mr-3 text-2xl">⏳</span>
                        Submitting Your Review...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center">
                        <span className="mr-2">📤</span>
                        Submit Review
                      </span>
                    )}
                  </button>
                </form>
              </div>
            )}

            {/* Existing Reviews */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border-l-4 border-blue-500">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                <span className="bg-blue-500 text-white p-2 rounded-lg mr-3">💬</span>
                All Reviews ({reviews.length})
              </h2>

              {reviews.length === 0 ? (
                <div className="text-center py-16 bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl">
                  <div className="text-7xl mb-4">🌟</div>
                  <p className="text-gray-600 text-lg font-semibold mb-2">No reviews yet</p>
                  <p className="text-gray-500">Be the first to share your experience!</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {reviews.map((review, index) => (
                    <div
                      key={review.id}
                      className="bg-gradient-to-r from-white to-blue-50 rounded-xl p-6 border-2 border-gray-200 hover:border-blue-400 hover:shadow-lg transition-all duration-300"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-start gap-4">
                          {/* Avatar */}
                          <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold shadow-lg">
                            {review.reviewerName.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-800 text-lg">
                              {review.reviewerName}
                            </h3>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-semibold">
                                {review.reviewerRole || 'User'}
                              </span>
                              <span className="text-gray-400">•</span>
                              <p className="text-sm text-gray-500">
                                {review.date}
                              </p>
                            </div>
                          </div>
                        </div>
                        {/* Rating Stars */}
                        <div className="flex items-center bg-yellow-100 px-3 py-2 rounded-xl border-2 border-yellow-300">
                          {[...Array(5)].map((_, i) => (
                            <span
                              key={i}
                              className={`text-xl ${
                                i < review.rating ? 'text-yellow-500' : 'text-gray-300'
                              }`}
                            >
                              ★
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Review Comment */}
                      <p className="text-gray-700 leading-relaxed mb-4 pl-[4.5rem]">
                        {review.comment}
                      </p>

                      {/* Review Actions */}
                      <div className="flex items-center space-x-6 text-sm pl-[4.5rem] border-t border-gray-200 pt-4">
                        <button className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors">
                          <span className="text-lg">👍</span>
                          <span className="font-medium">Helpful ({review.helpful || 0})</span>
                        </button>
                        <button className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors">
                          <span className="text-lg">💬</span>
                          <span className="font-medium">Reply</span>
                        </button>
                        <button className="flex items-center gap-2 text-gray-600 hover:text-red-600 transition-colors">
                          <span className="text-lg">🚩</span>
                          <span className="font-medium">Report</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewPage;
